import { Injectable, Type } from '@angular/core';

import { BehaviorSubject } from 'rxjs';

import * as moment from 'moment';

import { AcuityEnterCurrentComponent } from 'src/app/workflow/acuity/enter-current/enter-current.component';
import { AcuityReviewPreviousComponent } from 'src/app/workflow/acuity/review-previous/review-previous.component';
import { Constants } from 'src/shared/utilities/constants.util';
import { InpatientEnterCurrentComponent } from 'src/app/workflow/inpatient/enter-current/enter-current.component';
import { EnterReasonsComponent } from 'src/app/workflow/enter-reasons/enter-reasons.component';
import { InpatientReviewPreviousComponent } from 'src/app/workflow/inpatient/review-previous/review-previous.component';
import { ShiftBlock } from 'src/shared/models/shift-block.model';
import { ShiftRecord } from '../models/shift-record.model';
import { WorkflowPage } from 'src/shared/models/workflow-page.model';
import { WorkflowTemplate } from 'src/shared/models/workflow-template.model';
import { ReviewPreviousRoomsComponent } from 'src/app/workflow/acuity-rooms/review-previous-rooms/review-previous-rooms.component';
import { EnterCurrentRoomsComponent } from 'src/app/workflow/acuity-rooms/enter-current-rooms/enter-current-rooms.component';
import { EnterUOSComponent } from 'src/app/workflow/enter-uos/enter-uos.component';
import { ShiftRecordServiceInterface } from './shift-record-service.interface';
import { TotalUosEnterActualComponent } from 'src/app/workflow/total-uos/enter-actual/enter-actual-uos.component';
import { TotalUOSReviewPreviousByBlockComponent } from 'src/app/workflow/total-uos/review-previous/review-previous-uos.component';
import { WorkflowPageComponent } from 'src/app/workflow/base-classes/workflow-page/workflow-page.component';
import { UnitsOfServiceService } from './units-of-service.service';
import { AncillaryWorkflowPage } from '../models/workflow-page-ancllary.model';
import { UnitsOfService } from '../models/units-of-service.model';
import { ShiftRecordStatus } from '../models/shift-record-status';
import { TotalUOSEnterCurrentComponent } from 'src/app/workflow/total-uos/enter-current/enter-current-uos.component';
import { TotalUosEnterReasonComponent } from 'src/app/workflow/total-uos/enter-reason/enter-reason-uos.component';
import { Client } from '../models/client.model';
import { MiscUtil } from 'src/shared/utilities/misc.util';

@Injectable({
    providedIn: 'root'
})
export class WorkflowPlannerService {
    private formValidation = new BehaviorSubject<boolean>(null);
    formValidation$ = this.formValidation.asObservable();

    currentPage: WorkflowPage;
    shiftRecord: ShiftRecord;
    workflow: WorkflowTemplate;
    previousShiftDate : string = "" ;

    constructor(
        private _unitsOfServiceService: UnitsOfServiceService) {
        this.announceValidation(false);
    }

    announceValidation(isFormValid: boolean) {
        this.formValidation.next(isFormValid);
    }

    private configure(shiftBlock: ShiftBlock, date: string) {             
        if (!shiftBlock) {
            return;
        }

        this.workflow.costCenterId = shiftBlock.costCenterId;
        this.workflow.shiftBlockId = shiftBlock.id;
        this.workflow.workflowTimeFacility = this.workflowTime(shiftBlock, date);        
    }

    async configureAcuityWorkflow(shiftRecordService: ShiftRecordServiceInterface, shiftBlock: ShiftBlock, workflowTimeFacility: string, enterUosByBlock: boolean, client: Client, previousShift: string="") {
        return this.configureAncillaryWorkflow(shiftRecordService, shiftBlock, workflowTimeFacility, enterUosByBlock, 'Acuity', AcuityEnterCurrentComponent, AcuityReviewPreviousComponent, previousShift, client);
    }

    async configureAcuityWithRoomsWorkflow(shiftRecordService: ShiftRecordServiceInterface, shiftBlock: ShiftBlock, workflowTimeFacility: string, enterUosByBlock: boolean, client: Client, previousShift: string="") {  
        return this.configureAncillaryWorkflow(shiftRecordService, shiftBlock, workflowTimeFacility, enterUosByBlock, 'AcuityWithRooms', EnterCurrentRoomsComponent, ReviewPreviousRoomsComponent, previousShift, client);
    }

    async configureTotalUOSWorkflow(shiftRecordService: ShiftRecordServiceInterface, shiftBlock: ShiftBlock, 
         workflowTimeFacility: string, enterUosByBlock: boolean, client: Client, previousShift: string="", isLastShiftBlock: boolean) {
        if (!shiftBlock) {
            return;
        }

        if(previousShift!==""){
            this.previousShiftDate = previousShift;
        }

        let shiftRecord: ShiftRecord;        

        if (workflowTimeFacility) {
            shiftRecord = await this.getShiftRecord(shiftRecordService, shiftBlock.costCenterId, shiftBlock.id, this.workflowTime(shiftBlock, workflowTimeFacility));
        }
        else {
            shiftRecord = await this.getShiftRecordByShiftBlock(shiftRecordService, shiftBlock);
        }

        if (!shiftRecord) {
            return;
        }

        let unitOfService = await this._unitsOfServiceService.GetUnitsOfServiceById(shiftRecord.costCenter.ancillaryConfiguration.unitOfServiceId);

        let enterCurrent = new AncillaryWorkflowPage(
            TotalUOSEnterCurrentComponent,
            shiftRecord,
            client,
            {
                completedStatus: ShiftRecordStatus.EnterCurrent,
                pageNumber: 4,
                title: 'Enter Current Information',
                usePreviousShiftRecordInHeader: false,
                unitOfServiceName: unitOfService.category
            }
        );
        
        let firstPage = this.getTotalUosFirstPage(enterUosByBlock, shiftRecord, unitOfService, client, isLastShiftBlock);
        let secondPage = this.getTotalUosSecondPage(enterUosByBlock, shiftRecord, unitOfService, client);
        let thirdPage = this.getTotalUosThirdPage(enterUosByBlock, shiftRecord, client);

        firstPage.nextPage = secondPage;
        secondPage.previousPage = firstPage;
        secondPage.nextPage = thirdPage;
        thirdPage.previousPage = secondPage;
        thirdPage.nextPage = enterCurrent
        enterCurrent.previousPage = thirdPage;

        this.workflow = new WorkflowTemplate({ name: 'TotalUOS', pages: [ firstPage, secondPage, thirdPage, enterCurrent ]}); 

        this.configure(shiftBlock, workflowTimeFacility);
    }

    private getTotalUosFirstPage(enterUosByBlock: boolean, shiftRecord: ShiftRecord, unitOfService: UnitsOfService, client: Client, isLastShiftBlock: boolean): AncillaryWorkflowPage {
        let firstWorkflowPage = null;
        if (enterUosByBlock) {
            firstWorkflowPage = new AncillaryWorkflowPage(
                TotalUOSReviewPreviousByBlockComponent,
                shiftRecord,
                client,
                { 
                    completedStatus: ShiftRecordStatus.ReviewPrevious, 
                    pageNumber: 1, 
                    title: 'Review Previous Block',
                    usePreviousShiftRecordInHeader: true,
                    unitOfServiceName: unitOfService.category,
                    isLastShiftBlock: isLastShiftBlock
                }
            );
        }
        else {
            firstWorkflowPage = new AncillaryWorkflowPage(
                EnterUOSComponent, 
                shiftRecord, 
                client,
                { 
                    completedStatus: ShiftRecordStatus.ReviewPrevious,
                    pageNumber: 1, 
                    title: 'Review Previous Working Day',
                    usePreviousShiftRecordInHeader: false, 
                    usePreviousDayInHeader: true,
                    isSkippable: true,
                    unitOfServiceName: unitOfService.category
                });
        }       

        return firstWorkflowPage;
    }

    private getTotalUosSecondPage(enterUosByBlock: boolean, shiftRecord: ShiftRecord, unitOfService: UnitsOfService, client: Client): AncillaryWorkflowPage {
        let secondWorkflowPage = null;

        if (enterUosByBlock) {
            secondWorkflowPage = new AncillaryWorkflowPage(
                TotalUosEnterActualComponent,
                shiftRecord,
                client,
                {
                    completedStatus: ShiftRecordStatus.ReviewPreviousActuals,
                    pageNumber: 2,
                    title: 'Review Previous Block',
                    usePreviousShiftRecordInHeader: true,
                    unitOfServiceName: unitOfService.category
                });
        }
        else {
            secondWorkflowPage = new AncillaryWorkflowPage(
                TotalUosEnterActualComponent,
                shiftRecord,
                client,
                {
                    completedStatus: ShiftRecordStatus.ReviewPreviousActuals,
                    isSkippable: true,
                    pageNumber: 2,
                    title: 'Review Previous Working Day',
                    unitOfServiceName: unitOfService.category,
                    usePreviousDayInHeader: true
                });
        }       

        return secondWorkflowPage;
    }

    private getTotalUosThirdPage(enterUosByBlock: boolean, shiftRecord: ShiftRecord, client: Client): AncillaryWorkflowPage {
        let thirdWorkflowPage = null;

        if (enterUosByBlock) {
            thirdWorkflowPage = new AncillaryWorkflowPage(
                TotalUosEnterReasonComponent,
                shiftRecord,
                client,
                { 
                    completedStatus: ShiftRecordStatus.EnterReasons, 
                    pageNumber: 3,                 
                    title: 'Review Previous Block',
                    usePreviousShiftRecordInHeader: true
                });
        }        
        else {
            thirdWorkflowPage = new AncillaryWorkflowPage(
                TotalUosEnterReasonComponent,
                shiftRecord,
                client,
                { 
                    completedStatus: ShiftRecordStatus.EnterReasons, 
                    isSkippable: true,
                    pageNumber: 3,                 
                    title: 'Review Previous Working Day',
                    usePreviousDayInHeader: true
                });
        }

        return thirdWorkflowPage;
    }
    
    private async configureAncillaryWorkflow(shiftRecordService: ShiftRecordServiceInterface, shiftBlock: ShiftBlock, 
                                            workflowTimeFacility: string, enterUosByBlock: boolean, workflowName: string,
                                            enterCurrentComponent: Type<WorkflowPageComponent>, reviewPreviousComponent: Type<WorkflowPageComponent>,
                                            previousShift: string, client: Client) {
        if (!shiftBlock) {
            return;
        }

        if(previousShift!==""){
            this.previousShiftDate = previousShift;
        }

        let shiftRecord: ShiftRecord;        

        if (workflowTimeFacility) {
            shiftRecord = await this.getShiftRecord(shiftRecordService, shiftBlock.costCenterId, shiftBlock.id, this.workflowTime(shiftBlock, workflowTimeFacility));
        }
        else {
            shiftRecord = await this.getShiftRecordByShiftBlock(shiftRecordService, shiftBlock);
        }

        if (!shiftRecord) {
            return;
        }

        let unitOfService = await this._unitsOfServiceService.GetUnitsOfServiceById(shiftRecord.costCenter.ancillaryConfiguration.unitOfServiceId);

        let reasons = new WorkflowPage(
            EnterReasonsComponent, 
            shiftRecord,
            client,
            { 
                completedStatus: ShiftRecordStatus.EnterReasons, 
                pageNumber: 4,                 
                title: 'Enter Reasons for Variance' ,
                usePreviousShiftRecordInHeader: false
            }
        );

        let enterCurrent = new AncillaryWorkflowPage(
            enterCurrentComponent,
            shiftRecord,
            client,
            {
                completedStatus: ShiftRecordStatus.EnterCurrent,
                pageNumber: 3,
                title: 'Enter Current Information',
                usePreviousShiftRecordInHeader: false,
                unitOfServiceName: unitOfService.category
            }
        );

        let reviewPrevious = new AncillaryWorkflowPage(
            reviewPreviousComponent,
            shiftRecord,
            client,
            { 
                completedStatus: ShiftRecordStatus.ReviewPrevious, 
                pageNumber: 2, 
                title: 'Review Previous Block',
                usePreviousShiftRecordInHeader: true,
                unitOfServiceName: unitOfService.category
            }
        );

        let enterUOS = new AncillaryWorkflowPage(
            EnterUOSComponent, 
            shiftRecord, 
            client,
            { 
                completedStatus: 'EnterUOS',
                 pageNumber: 1, 
                 title:  this.getEnterUOSTitle(enterUosByBlock),
                 usePreviousShiftRecordInHeader: enterUosByBlock, 
                 usePreviousDayInHeader: !enterUosByBlock,
                 isSkippable: !enterUosByBlock,
                 unitOfServiceName: unitOfService.category
            });

        enterUOS.nextPage = reviewPrevious;
        reviewPrevious.previousPage = enterUOS;
        reviewPrevious.nextPage = enterCurrent;
        enterCurrent.previousPage = reviewPrevious;
        enterCurrent.nextPage = reasons;
        reasons.previousPage = enterCurrent;

        this.workflow = new WorkflowTemplate({ name: workflowName, pages: [ enterUOS, reviewPrevious, enterCurrent, reasons ]}); 

        this.configure(shiftBlock, workflowTimeFacility);
    }
    
    async configureInpatientWorkflow(shiftRecordService: ShiftRecordServiceInterface, shiftBlock: ShiftBlock, workflowTimeFacility: string, previousShift: string, client: Client) { 
        if (!shiftBlock) {
            return;
        }
        
        let shiftRecord: ShiftRecord;   
        
        if(previousShift!=""){
            this.previousShiftDate = previousShift;
        }
        if (workflowTimeFacility) {
            shiftRecord = await this.getShiftRecord(shiftRecordService, shiftBlock.costCenterId, shiftBlock.id, this.workflowTime(shiftBlock, workflowTimeFacility));
        }
        else {
            shiftRecord = await this.getShiftRecordByShiftBlock(shiftRecordService, shiftBlock);
        }

        if (!shiftRecord) {
            return;
        }

        let reasons = new WorkflowPage(
                        EnterReasonsComponent,
                        shiftRecord, 
                        client,
                        { 
                            completedStatus: 'EnterReasons', 
                            pageNumber: 3,                             
                            title: 'Enter Reasons for Variance' ,
                            usePreviousShiftRecordInHeader: false
                        });

        let enterCurrent = new WorkflowPage(
                            InpatientEnterCurrentComponent, 
                            shiftRecord,
                            client,
                            { 
                                completedStatus: 'EnterCurrent',                                 
                                pageNumber: 2, 
                                title: 'Enter Current Information',
                                usePreviousShiftRecordInHeader: false 
                            });

        let reviewPrevious = new WorkflowPage(
                                InpatientReviewPreviousComponent,
                                shiftRecord,
                                client,
                                { 
                                    completedStatus: 'ReviewPrevious', 
                                    pageNumber: 1, 
                                    title: 'Review Previous Block',
                                    usePreviousShiftRecordInHeader: true
                                });

        reviewPrevious.nextPage = enterCurrent;
        enterCurrent.nextPage = reasons;

        enterCurrent.previousPage = reviewPrevious;
        reasons.previousPage = enterCurrent;

        this.workflow = new WorkflowTemplate({ name: 'Inpatient Nursing', pages: [ reviewPrevious, enterCurrent, reasons ]});  
        
        this.configure(shiftBlock, workflowTimeFacility);
    }
    
    private getEnterUOSTitle(enterUosByBlock: boolean) : string {
        return 'Review Previous ' + (enterUosByBlock ? 'Block' : 'Working Day');
    }
    
    private getLastCompletedPage(): WorkflowPage {        
        let pages = this.workflow.pages;
        let leftOffPages = pages.filter(p => p.completedStatus == (p.shiftRecord ? p.shiftRecord.status : null));        
        
        return leftOffPages.length > 0 ? leftOffPages[0] : null;
    }
    
    private async getShiftRecord(shiftRecordService: ShiftRecordServiceInterface, costCenterId: number, shiftBlockId: number, workflowTimeFacility: string): Promise<ShiftRecord> {
        return await shiftRecordService.getActive(costCenterId, shiftBlockId, workflowTimeFacility,this.previousShiftDate);
    }

    private async getShiftRecordByShiftBlock(shiftRecordService: ShiftRecordServiceInterface, shiftBlock: ShiftBlock): Promise<ShiftRecord> {
        return await this.getShiftRecord(shiftRecordService, shiftBlock.costCenterId, shiftBlock.id, this.workflowTime(shiftBlock));
    }    

    private isConfigured(): boolean {
        return this.workflow.costCenterId && this.workflow.shiftBlockId && !!this.workflow.workflowTimeFacility;
    }

    isWorkflowValid(): boolean {
        if (!this.workflow || !this.workflow.pages || this.workflow.pages.length == 0 || !this.isConfigured()) {
            return false;
        }

        return true;
    }

    next() {  
        this.setPage(this.currentPage.nextPage);                     
    }

    previous() {
        this.setPage(this.currentPage.previousPage);
    }

    skip() {
        this.setPage(this.currentPage.nextPage);
    }

    private setPage(pendingPage: WorkflowPage) {
        if (!pendingPage) {
            return;
        }      

        let nextPage = this.currentPage.nextPage ? this.currentPage.nextPage : null;
        let previousPage = this.currentPage.previousPage ? this.currentPage.previousPage : null;

        if ((nextPage && nextPage.pageNumber == pendingPage.pageNumber) || (previousPage && previousPage.pageNumber == pendingPage.pageNumber)) {
            this.currentPage = pendingPage;
        }
    }

    start(): WorkflowPage {
        let pages = this.workflow.pages;
        let lastCompletedPage = this.getLastCompletedPage();

        if (lastCompletedPage) {
            this.currentPage = lastCompletedPage;
        }
        else {
            this.currentPage = pages[0];
        }

        return this.currentPage;
    }  

    private workflowTime(shiftBlock: ShiftBlock, date: string = null) {
        if (date) {
            return MiscUtil.dateStartHourToFacilityTimeFormat(date,shiftBlock.startHour);
        }
        else {
            return moment().local().hour(shiftBlock.startHour).minute(0).second(0).format(Constants.FacilityTimeFormat);
        }
    }
}